import { useEffect, useState } from 'react';
import { Copy, MessageCircle } from 'lucide-react';
import { fmtDateTime } from '../lib/format';
import { Alert, Button, Input, Modal, useToast } from './ui';

/** Enlace de acceso o invitación para compartir a mano (sobre todo cuando no hay email configurado). */
export interface ShareLink {
  title: string;
  intro: string;
  url: string;
  email?: string | null;
  expiresAt?: string | null;
  emailDelivered?: boolean;
}

// Se puede abrir desde cualquier pantalla, aunque la navegación cambie justo después.
const listeners = new Set<(l: ShareLink) => void>();
export function showShareLink(link: ShareLink) {
  listeners.forEach((fn) => fn(link));
}

export function ShareLinkHost() {
  const [link, setLink] = useState<ShareLink | null>(null);
  const toast = useToast();
  useEffect(() => {
    listeners.add(setLink);
    return () => void listeners.delete(setLink);
  }, []);
  if (!link) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar: selecciona el enlace y cópialo a mano.');
    }
  };
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${link.intro}\n${link.url}`)}`;
  return (
    <Modal open onClose={() => setLink(null)} title={link.title} footer={<Button onClick={() => setLink(null)}>Hecho</Button>}>
      <div className="space-y-3 text-sm">
        <p>{link.intro}</p>
        {link.emailDelivered ? (
          <Alert tone="success">Se ha enviado por email{link.email ? ` a ${link.email}` : ''}. También puedes compartirlo tú.</Alert>
        ) : (
          <Alert tone="warning">El email no está configurado en esta instalación: envía tú este enlace{link.email ? ` a ${link.email}` : ''} (WhatsApp, correo…).</Alert>
        )}
        <div className="flex gap-2">
          <Input readOnly value={link.url} aria-label="Enlace" onFocus={(e) => e.currentTarget.select()} />
          <Button variant="secondary" icon={<Copy className="h-4 w-4" />} onClick={copy}>
            Copiar
          </Button>
        </div>
        <a href={whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:underline">
          <MessageCircle className="h-4 w-4" /> Enviar por WhatsApp
        </a>
        <p className="text-xs text-slate-500">Es personal y de un solo uso{link.expiresAt ? `; caduca el ${fmtDateTime(link.expiresAt)}` : ''}. No lo publiques.</p>
      </div>
    </Modal>
  );
}
